import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  faArrowDownToLine,
  faPrint,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import { cn } from '@repo/videa-components/src/lib/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { Link, Redirect, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';

import { LoadingAnimation } from '@/common-components/animations/loading/LoadingAnimation';
import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import { useGetDateFNSLocale } from '@/locales/helpers';
import { claimAttachmentIdAtom } from '@/modules/claims/freeClaims/atoms';
import {
  PageProvider,
  usePageContext,
} from '@/modules/claims/freeClaims/context/PageContext';
import { useClaimSummary } from '@/modules/claims/freeClaims/hooks/useClaimSummary';
import { useTreatmentsToUse } from '@/modules/claims/freeClaims/hooks/useTreatmentsToUse';
import { useShouldHideHuddle } from '@/modules/patientList/useShouldHideHuddle';
import { useHasClaimAttachmentWritebackSupport } from '@/react-query/rcm/useHasClaimAttachmentWritebackSupport';
import { useGetNarrative } from '@/react-query/rcm/useNarrative';
import { useDownloadPieces } from '@/react-query/useDownloadPieces';
import {
  type Appointment,
  useAppointmentsByVisitId,
} from '@/react-query/useAppointments';
import { useGeneratePdf } from '@/react-query/useGeneratePdf';
import { usePreferences } from '@/react-query/usePreferences';
import { usePushToPMS } from '@/react-query/usePushToPMS';
import { useWritebackStatus } from '@/react-query/useWritebackStatus';
import routesConstants from '@/routes/routes.constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ZoomToolTopbar } from '../../../../../common-components/elements/ZoomToolTopbar.tsx/ZoomToolTopbar';
import {
  claimAttachmentFmxImagesAtom,
  claimAttachmentFmxTableDataAtom,
  claimAttachmentToothLevelImagesAtom,
  claimNotesAtom,
  claimWarningsAtom,
  cleanClaimsAttachmentValuesAtom,
  clinicalNotesAtom,
  excludeFmxFindingsWhenPeriochartPresentAtom,
  insPagesAtom,
  isNarrativeLoadingAtom,
  narrativeAtom,
  narrativeDevToolDataAtom,
  narrativeTraceIdAtom,
  selectedTreatmentCodesAtom,
  selectedTreatmentsAtom,
} from '../../../freeClaims/state';
import {
  filterImagesByTreatments,
  generateFmxTitle,
  getEnabledAttachmentViews,
  getUniqueClaimNotesForSelectedTxs,
  getUniqueClaimWarningsForSelectedTxs,
  sortImagesByDisplayItem,
  transformFmxImagesForPdf,
  transformToothLevelImagesForPdf,
} from '../../helpers';

import { ClaimAttachmentContent } from './claimAttachment/ClaimAttachmentContent';
import { ClaimCustomizationSidebar } from './claimCustomization/ClaimCustomizationSidebar';
import { NarrativeDevTool } from './NarrativeDevTool';

const RATE_ATTACHMENT_OPTIONS_UP = [
  {
    value: 'accurate-information',
    label: <Trans>Accurate information</Trans>,
  },
  {
    value: 'minimal-edits',
    label: <Trans>Minimal edits needed</Trans>,
  },
  {
    value: 'saved-time',
    label: <Trans>Saved time</Trans>,
  },
  {
    value: 'correct-terminology',
    label: <Trans>Correct terminology</Trans>,
  },
  {
    value: 'correct-images',
    label: <Trans>Correct images selected</Trans>,
  },
];

const RATE_ATTACHMENT_OPTIONS_DOWN = [
  {
    value: 'missing-treatments',
    label: <Trans>Missing treatments</Trans>,
  },
  {
    value: 'incorrect-codes',
    label: <Trans>Incorrect codes</Trans>,
  },
  {
    value: 'mislabeled-images',
    label: <Trans>Mislabeled images</Trans>,
  },
  {
    value: 'incorrect-info',
    label: <Trans>Incorrect patient or provider information</Trans>,
  },
];

// Create a wrapper component that provides the context
const ClaimSummaryPageWrapper = () => {
  return (
    <PageProvider>
      <ClaimSummaryPage />
    </PageProvider>
  );
};

const ClaimSummaryPage = () => {
  // refs
  const contentRef = useRef<HTMLDivElement>(null);
  const hasShownResultForBatchIdRef = useRef<string | null>(null);
  const pushTreatmentCodesRef = useRef<string[]>([]);

  // URL params (source of truth for which claim is open; supports refresh / deep link)
  const params = useParams<{ unifiedId?: string; estimatedVisitId?: string }>();
  const unifiedIdParam = params.unifiedId;
  const estimatedVisitIdParam = params.estimatedVisitId;

  const estimatedVisitIdNum = parseInt(estimatedVisitIdParam ?? '', 10);
  const hasValidParams =
    !!unifiedIdParam &&
    !!estimatedVisitIdParam &&
    Number.isFinite(estimatedVisitIdNum);

  const { primaryPracticeId: practiceId } = useSelector(
    (state: { login?: { user?: { primaryPracticeId?: string } } }) =>
      state.login?.user ?? {},
  );

  const { data: appointmentData, isError: appointmentError } =
    useAppointmentsByVisitId(estimatedVisitIdNum, practiceId ?? '');

  // atoms
  const [insPage, setInsPage] = useAtom(insPagesAtom);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useAtom(
    selectedTreatmentsAtom,
  );
  const setCleanClaimsAttachmentValues = useSetAtom(
    cleanClaimsAttachmentValuesAtom,
  );

  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const selectedTreatmentCodes = useAtomValue(selectedTreatmentCodesAtom);
  const excludeFmxFindingsWhenPeriochartPresent = useAtomValue(
    excludeFmxFindingsWhenPeriochartPresentAtom,
  );

  const setNarrative = useSetAtom(narrativeAtom);
  const setNarrativeDevToolData = useSetAtom(narrativeDevToolDataAtom);
  const setNarrativeTraceId = useSetAtom(narrativeTraceIdAtom);
  const setIsNarrativeLoading = useSetAtom(isNarrativeLoadingAtom);
  const setClaimAttachmentId = useSetAtom(claimAttachmentIdAtom);
  const setSelectedTreatmentCodes = useSetAtom(selectedTreatmentCodesAtom);
  const setClaimNotes = useSetAtom(claimNotesAtom);
  const setClaimWarnings = useSetAtom(claimWarningsAtom);

  // state
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);
  const [hasInitiallyScaled, setHasInitiallyScaled] = useState(false);
  const [writebackBatchId, setWritebackBatchId] = useState<string | null>(null);
  // hooks
  const locale = useGetDateFNSLocale();

  // Hydrate atom from URL + appointment when loaded (so patientInfo and rest of UI are correct)
  useEffect(() => {
    if (!hasValidParams || appointmentError) {
      return;
    }
    if (appointmentData) {
      const appointment: Appointment = {
        ...appointmentData,
        lastTreatments: {},
      };
      setCleanClaimsAttachmentValues({
        unifiedId: unifiedIdParam,
        estimatedVisitId: estimatedVisitIdParam,
        appointment,
        locale,
      });
    } else {
      setCleanClaimsAttachmentValues({
        unifiedId: unifiedIdParam,
        estimatedVisitId: estimatedVisitIdParam,
        locale,
      });
    }
  }, [
    hasValidParams,
    unifiedIdParam,
    estimatedVisitIdParam,
    appointmentData,
    appointmentError,
    locale,
    setCleanClaimsAttachmentValues,
  ]);

  // Prefer URL params so useClaimSummary runs on load/refresh before atom is hydrated
  const effectiveUnifiedId =
    unifiedIdParam ?? cleanClaimsAttachmentValues.unifiedId;
  const effectiveEstimatedVisitId =
    estimatedVisitIdParam ?? cleanClaimsAttachmentValues.estimatedVisitId ?? '';

  // hooks (must all run before any conditional return to satisfy rules of hooks)
  const { data: preferences } = usePreferences();
  const { unifiedId, estimatedVisitId = '' } = cleanClaimsAttachmentValues;
  const { t } = useLingui();
  const { componentRef } = usePageContext();
  const { claimSummary, isLoadingClaimSummary } = useClaimSummary({
    estimatedVisitId: parseInt(effectiveEstimatedVisitId, 10),
    unifiedAppointmentId: parseInt(effectiveUnifiedId, 10),
  });
  const { trackEvent } = useTrackEvent();
  const hideHuddle = useShouldHideHuddle();
  const isClaimAttachmentEnabled = preferences?.claimAttachmentEnabled;
  const hasClaimAttachmentWritebackSupport =
    useHasClaimAttachmentWritebackSupport().data ?? false;
  const shouldShowPushToPMS = hasClaimAttachmentWritebackSupport;

  const handleAttachmentButtonClick = (rating: 'up' | 'down') => {
    // Only track up ratings immediately - down ratings will be tracked when feedback is submitted
    if (rating === 'up') {
      trackEvent(mixpanelEvents.rateClaimAttachment, {
        txCode: selectedTreatmentCodes,
        source: 'attachment',
        rating,
      });
    }
  };

  const handleSubmitAttachmentFeedback = (
    rating: 'up' | 'down',
    selectedOptions: string[],
    additionalComments?: string,
  ) => {
    // TODO BOSON-319: Send rating to UserPilot

    // Track down feedback when submitted (up feedback was already tracked on button click)
    if (rating === 'down') {
      trackEvent(mixpanelEvents.rateClaimAttachment, {
        txCode: selectedTreatmentCodes,
        source: 'attachment',
        rating,
        reason: selectedOptions.join(', ') || undefined,
        comment: additionalComments || undefined,
      });
    }
  };

  const { isLoading: isLoadingNarrative, isFetching: isFetchingNarrative } =
    useGetNarrative(
      parseInt(estimatedVisitId),
      selectedTreatmentIds?.map((tx) => Number(tx)) ?? [],
      parseInt(unifiedId),
      {
        onError() {
          setNarrative('None provided.');
          setNarrativeDevToolData(undefined);
          setNarrativeTraceId(undefined);
          setIsNarrativeLoading(false);
        },
        onSuccess(data) {
          setNarrative(data.narrative);
          setNarrativeDevToolData(data.devToolData);
          setNarrativeTraceId(data.traceId);
          setIsNarrativeLoading(false);
        },
        retry: true,
        enabled: (selectedTreatmentIds?.length ?? 0) > 0,
      },
    );

  // Atoms for Print Version button
  const {
    mutate: generatePdf,
    isLoading: isPdfLoading,
    reset: resetPdfMutation,
  } = useGeneratePdf();
  const {
    mutate: pushToPMS,
    isLoading: isPushingToPMS,
    reset: resetPushToPMSMutation,
  } = usePushToPMS();
  const {
    mutate: downloadPieces,
    isLoading: isDownloadingPieces,
  } = useDownloadPieces();
  const { data: writebackStatus } = useWritebackStatus(writebackBatchId, unifiedId);
  const images = useAtomValue(claimAttachmentToothLevelImagesAtom);
  const fmxImages = useAtomValue(claimAttachmentFmxImagesAtom);
  const fmxTableData = useAtomValue(claimAttachmentFmxTableDataAtom);
  const clinicalNote = useAtomValue(clinicalNotesAtom);
  const narrative = useAtomValue(narrativeAtom);
  const attachmentID = useAtomValue(claimAttachmentIdAtom);

  // Get original images for PDF generation
  const { colorImages: filteredColorImages, radiographs: filteredRadiographs } =
    useMemo(() => {
      const filteredImages = filterImagesByTreatments(
        claimSummary?.images ?? [],
        claimSummary?.imagesDisplay ?? [],
        selectedTreatmentIds,
      );

      const colorImages = filteredImages?.filter(
        (img) => img.imageType === 'COLOR_IMAGE',
      );

      const radiographs = filteredImages?.filter(
        (img) => img.imageType !== 'COLOR_IMAGE',
      );

      return {
        colorImages: sortImagesByDisplayItem(
          colorImages,
          claimSummary?.imagesDisplay ?? [],
        ),
        radiographs: sortImagesByDisplayItem(
          radiographs,
          claimSummary?.imagesDisplay ?? [],
        ),
      };
    }, [
      claimSummary?.images,
      claimSummary?.imagesDisplay,
      selectedTreatmentIds,
    ]);

  // Generate FMX title
  const fmxTitle = useMemo(() => {
    return generateFmxTitle(claimSummary?.treatments, selectedTreatmentIds);
  }, [claimSummary?.treatments, selectedTreatmentIds]);

  // variables
  const { treatments: claimSummaryTreatments = [] } = claimSummary ?? {};

  const { treatmentsToUse } = useTreatmentsToUse({
    allTreatments: claimSummaryTreatments ?? [],
    selectedTreatmentIds: selectedTreatmentIds ?? [],
  });

  const { hasToothLevelSummary, hasQuadLevelSummary } =
    getEnabledAttachmentViews({
      selectedTreatmentIds,
      claimSummary,
    });

  const isActionsDisabled =
    isLoadingClaimSummary ||
    isLoadingNarrative ||
    isFetchingNarrative;

  const loadingMessages = [
    <Trans>Selecting high quality radiographs...</Trans>,
    <Trans>Extracting clinical findings...</Trans>,
    <Trans>Crafting narrative...</Trans>,
  ];

  const handleSelectTreatments = useCallback(
    (txIds: string[]) => {
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(
          treatmentsToUse
            .filter((tx) => txIds.includes(tx.txId.toString()))
            .map((tx) => tx.txCode),
        ),
      ]);
    },
    [setSelectedTreatmentIds, setSelectedTreatmentCodes, treatmentsToUse],
  );

  // Transform data for PDF generation
  const transformedToothLevelImagesForPdf = useMemo(() => {
    return transformToothLevelImagesForPdf(images);
  }, [images]);

  const transformedFmxImagesForPdf = useMemo(() => {
    return transformFmxImagesForPdf(
      claimSummary,
      selectedTreatmentIds,
      fmxImages,
    );
  }, [claimSummary, selectedTreatmentIds, fmxImages]);

  // Simplified PDF generation handler
  const handleGeneratePdf = useCallback(() => {
    trackEvent(mixpanelEvents.generatePdf, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });

    // Show warning toast about potential wait time and store its ID
    const warningToastId = toast(
      <Toast
        variant="warning"
        title={t`Generating PDF...`}
        description={t`This may take up to a minute. Please wait while we process your request.`}
      />,
      {
        closeButton: true,
        position: 'top-right',
        style: { width: '460px' },
      },
    );

    generatePdf(
      {
        unifiedAppointmentId: unifiedId,
        attachmentID,
        narrative,
        clinicalNote,
        toothLevelImages: transformedToothLevelImagesForPdf,
        fmxImages: transformedFmxImagesForPdf,
        fmxTableData,
        fmxTitle,
        includeFmxFindings: !excludeFmxFindingsWhenPeriochartPresent,
      },
      {
        onSuccess: () => {
          // Dismiss the warning toast
          toast.dismiss(warningToastId);

          toast(
            <Toast
              variant="success"
              title={t`PDF generated successfully!`}
              description={t`Your claim attachment PDF has been downloaded.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          // Reset the mutation state to ensure button text resets
          resetPdfMutation();
        },
        onError: (error) => {
          console.error('Failed to generate PDF:', error);

          // Dismiss the warning toast
          toast.dismiss(warningToastId);

          toast(
            <Toast
              variant="error"
              title={t`PDF generation failed`}
              description={t`Failed to generate PDF. Please try again or contact support if the problem persists.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          // Reset the mutation state to ensure button text resets
          resetPdfMutation();
        },
      },
    );
  }, [
    unifiedId,
    attachmentID,
    narrative,
    clinicalNote,
    fmxTableData,
    fmxTitle,
    transformedFmxImagesForPdf,
    transformedToothLevelImagesForPdf,
    generatePdf,
    resetPdfMutation,
    t,
    excludeFmxFindingsWhenPeriochartPresent,
    trackEvent,
    estimatedVisitId,
    selectedTreatmentCodes,
  ]);

  // Download button handler - downloads ZIP of PNG pieces from backend
  const handleDownloadPieces = useCallback(() => {
    if (selectedTreatmentCodes.length) {
      trackEvent(mixpanelEvents.downloadAttachment, {
        unifiedId,
        estimatedVisitId,
        txCode: selectedTreatmentCodes,
      });
    }

    downloadPieces(
      {
        unifiedAppointmentId: unifiedId,
        attachmentID,
        narrative,
        clinicalNote,
        fmxTitle,
        toothLevelImages: transformedToothLevelImagesForPdf,
        fmxImages: transformedFmxImagesForPdf,
        fmxTableData,
        includeFmxFindings: !excludeFmxFindingsWhenPeriochartPresent,
        patientName: cleanClaimsAttachmentValues.patientInfo?.name,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`Success`}
              description={t`ZIP downloaded successfully`}
            />,
            {
              closeButton: true,
              position: 'top-right',
            },
          );
        },
        onError: (error) => {
          console.error('Error downloading pieces:', error);
          toast(
            <Toast
              variant="error"
              title={t`Error`}
              description={t`Failed to download. Please try again.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
            },
          );
        },
      },
    );
  }, [
    selectedTreatmentCodes,
    trackEvent,
    unifiedId,
    estimatedVisitId,
    downloadPieces,
    attachmentID,
    narrative,
    clinicalNote,
    fmxTitle,
    transformedToothLevelImagesForPdf,
    transformedFmxImagesForPdf,
    fmxTableData,
    excludeFmxFindingsWhenPeriochartPresent,
    cleanClaimsAttachmentValues.patientInfo?.name,
    t,
  ]);

  // Push to PMS button handler - initiates push and starts status polling
  const handlePushToPMS = useCallback(() => {
    // Capture treatment codes for analytics when polling completes
    pushTreatmentCodesRef.current = selectedTreatmentCodes;

    // Track push started
    trackEvent(mixpanelEvents.pushToPMSStarted, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });

    pushToPMS(
      {
        unifiedAppointmentId: unifiedId,
        attachmentID,
        narrative,
        clinicalNote,
        fmxTitle,
        toothLevelImages: transformedToothLevelImagesForPdf,
        fmxImages: transformedFmxImagesForPdf,
        fmxTableData,
        includeFmxFindings: !excludeFmxFindingsWhenPeriochartPresent,
      },
      {
        onSuccess: (data) => {
          // Only set batchId to start polling - success/error handled by polling effect
          setWritebackBatchId(data.batchId);
          resetPushToPMSMutation();
        },
        onError: (error) => {
          console.error('Error pushing to PMS:', error);
          toast(
            <Toast
              variant="error"
              title={t`Error`}
              description={t`Failed to push to PMS. Please try again.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
            },
          );
          resetPushToPMSMutation();
        },
      },
    );
  }, [
    pushToPMS,
    unifiedId,
    attachmentID,
    narrative,
    clinicalNote,
    fmxTitle,
    transformedToothLevelImagesForPdf,
    transformedFmxImagesForPdf,
    fmxTableData,
    excludeFmxFindingsWhenPeriochartPresent,
    trackEvent,
    estimatedVisitId,
    selectedTreatmentCodes,
    resetPushToPMSMutation,
    t,
  ]);

  // useEffects
  // Handle cleanup on mount/unmount
  useEffect(() => {
    // Cleanup on unmount
    return () => {
      setClaimAttachmentId(undefined);
      setIsNarrativeLoading(false);
      setSelectedTreatmentIds([]);
      setSelectedTreatmentCodes([]);
      setWritebackBatchId(null);
    };
  }, [
    setClaimAttachmentId,
    setIsNarrativeLoading,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setWritebackBatchId,
  ]);

  // Handle writeback status polling results
  useEffect(() => {
    // Only proceed if we have a batchId and haven't shown result for this batch yet
    if (!writebackBatchId || hasShownResultForBatchIdRef.current === writebackBatchId) {
      return;
    }

    if (writebackStatus?.status === 'success') {
      hasShownResultForBatchIdRef.current = writebackBatchId;
      toast(
        <Toast
          variant="success"
          title={t`Success`}
          description={t`Successfully pushed to PMS`}
        />,
        {
          closeButton: true,
          position: 'top-right',
        },
      );
      trackEvent(mixpanelEvents.closeAttachmentEditor, {
        unifiedId,
        estimatedVisitId,
        entryPoint: 'Push to PMS',
        txCode: pushTreatmentCodesRef.current,
      });
      trackEvent(mixpanelEvents.pushToPMSCompleted, {
        status: 'success',
        batchId: writebackBatchId,
        unifiedId,
        estimatedVisitId,
        txCode: pushTreatmentCodesRef.current,
      });
    } else if (writebackStatus?.status === 'error') {
      hasShownResultForBatchIdRef.current = writebackBatchId;
      toast(
        <Toast
          variant="error"
          title={t`Error`}
          description={t`Failed to push to PMS. Some documents may not have been uploaded.`}
        />,
        {
          closeButton: true,
          position: 'top-right',
        },
      );
      trackEvent(mixpanelEvents.pushToPMSCompleted, {
        status: 'error',
        batchId: writebackBatchId,
        unifiedId,
        estimatedVisitId,
        txCode: pushTreatmentCodesRef.current,
      });
    }
  }, [
    writebackStatus?.status,
    writebackBatchId,
    trackEvent,
    unifiedId,
    estimatedVisitId,
    t,
  ]);

  // Show timeout toast if push stays pending for > 2 minutes
  useEffect(() => {
    if (!writebackBatchId) return;
    const timer = setTimeout(() => {
      if (hasShownResultForBatchIdRef.current !== writebackBatchId) {
        hasShownResultForBatchIdRef.current = writebackBatchId;
        toast(
          <Toast
            variant="error"
            title={t`Push to PMS timed out`}
            description={t`The push may still complete. Check your PMS or try again.`}
          />,
          {
            closeButton: true,
            position: 'top-right',
          },
        );
      }
    }, 130_000); // 130s — slightly after hook stops polling at ~120s
    return () => clearTimeout(timer);
  }, [writebackBatchId, t]);

  // Reset narrative, notes, and warnings when claim context changes so previous claim's content does not persist
  useEffect(() => {
    setNarrative('');
    setClaimNotes([]);
    setClaimWarnings([]);
    setIsNarrativeLoading(true);
    setSelectedTreatmentIds([]);
    setSelectedTreatmentCodes([]);
    setNarrativeDevToolData(undefined);
    setNarrativeTraceId(undefined);
  }, [
    unifiedId,
    estimatedVisitId,
    setNarrative,
    setClaimNotes,
    setClaimWarnings,
    setIsNarrativeLoading,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setNarrativeDevToolData,
    setNarrativeTraceId,
  ]);

  // Sync loading state with the hook
  useEffect(() => {
    if ((selectedTreatmentIds?.length ?? 0) > 0) {
      setIsNarrativeLoading(isLoadingNarrative || isFetchingNarrative);
    }
  }, [
    isLoadingNarrative,
    selectedTreatmentIds,
    isFetchingNarrative,
    setIsNarrativeLoading,
  ]);

  // Handle treatments and claim notes updates
  useEffect(() => {
    // Set selected treatments when data loads
    if (claimSummaryTreatments?.length && !selectedTreatmentIds?.length) {
      const txIds = claimSummaryTreatments.map((tx) => tx.txId.toString());
      setSelectedTreatmentIds(txIds);
      setSelectedTreatmentCodes([
        ...new Set(claimSummaryTreatments.map((tx) => tx.txCode)),
      ]);
    }

    // Update claim notes and warnings when treatments or summary changes
    if (
      claimSummary &&
      selectedTreatmentIds &&
      selectedTreatmentIds.length > 0
    ) {
      const notes = getUniqueClaimNotesForSelectedTxs(
        claimSummary,
        selectedTreatmentIds.map(Number),
      );
      setClaimNotes(notes);
      const warnings = getUniqueClaimWarningsForSelectedTxs(
        claimSummary,
        selectedTreatmentIds.map(Number),
      );
      setClaimWarnings(warnings);
    }

    // Update content height when content changes
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [
    claimSummaryTreatments,
    selectedTreatmentIds,
    claimSummary,
    setSelectedTreatmentIds,
    setSelectedTreatmentCodes,
    setClaimNotes,
    setClaimWarnings,
    contentRef,
    excludeFmxFindingsWhenPeriochartPresent,
  ]);

  // Update content height when scale changes
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [scale, contentRef, componentRef]);

  // Update content height when selected treatments change
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [selectedTreatmentIds, contentRef, componentRef, scale]);

  // Update content height when exclude FMX findings toggle changes
  useEffect(() => {
    if (contentRef.current && scale <= 1) {
      const element = componentRef.current as HTMLDivElement | null;
      if (element) {
        const height = element.scrollHeight * scale;
        setContentHeight(height);
      }
    }
  }, [
    excludeFmxFindingsWhenPeriochartPresent,
    contentRef,
    componentRef,
    scale,
  ]);

  // Redirect when URL params are missing or when appointment fetch failed (e.g. invalid id, 404)
  if (!hasValidParams) {
    return <Redirect to={routesConstants.CLAIMS} />;
  }
  if (appointmentError) {
    return <Redirect to={routesConstants.CLAIMS} />;
  }

  return (
    <div className="vai-w-screen vai-overflow-hidden vai-flex vai-flex-col vai-h-full">
      <div className="vai-px-6 vai-py-3 vai-bg-white vai-border-b vai-text-primary vai-flex vai-justify-between vai-items-center">
        {insPage === 'dev-tool' ? (
          <Button onClick={() => setInsPage('preview-single')} variant="ghost">
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="vai-mr-2 vai-text-primary"
            />
            Back to Claim Attachment
          </Button>
        ) : (
          <>
            <Link
              to={routesConstants.CLAIMS}
              onClick={() => {
                selectedTreatmentCodes?.length &&
                  trackEvent(mixpanelEvents.closeAttachmentEditor, {
                    unifiedId,
                    estimatedVisitId,
                    entryPoint: 'Top link',
                    txCode: selectedTreatmentCodes,
                  });
              }}
            >
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="vai-mr-2 vai-text-primary"
              />
              {hideHuddle ? (
                <Trans>All Patients</Trans>
              ) : (
                <Trans>Dashboard</Trans>
              )}
            </Link>
            <div className="vai-flex vai-gap-2 vai-items-center">
              {isClaimAttachmentEnabled && shouldShowPushToPMS && (
                <Button
                  disabled={isActionsDisabled}
                  loading={isPushingToPMS}
                  onClick={handlePushToPMS}
                >
                  <Trans>Push to PMS</Trans>
                  {writebackStatus &&
                    writebackStatus.status !== 'not_pushed' && (
                      <span
                        className={cn(
                          'vai-w-2 vai-h-2 vai-rounded-full vai-flex-shrink-0',
                          writebackStatus.status === 'success' &&
                            'vai-bg-green-500',
                          writebackStatus.status === 'error' &&
                            'vai-bg-red-500',
                          (writebackStatus.status === 'pending' ||
                            writebackStatus.status === 'in_progress') &&
                            'vai-bg-orange-400',
                        )}
                      />
                    )}
                </Button>
              )}
              {isClaimAttachmentEnabled && (
                <Button
                  disabled={isActionsDisabled}
                  loading={isDownloadingPieces}
                  title={t`Download`}
                  onClick={handleDownloadPieces}
                  variant="outline"
                >
                  <FontAwesomeIcon
                    className="vai-text-[#18181B]"
                    icon={faArrowDownToLine}
                  />
                </Button>
              )}
              {isClaimAttachmentEnabled && (
                <Button
                  disabled={isActionsDisabled}
                  loading={isPdfLoading}
                  title={t`Print Version`}
                  variant="outline"
                  onClick={() => {
                    handleGeneratePdf();
                  }}
                >
                  <FontAwesomeIcon
                    className="vai-text-[#18181B]"
                    icon={faPrint}
                  />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
      {isLoadingClaimSummary ? (
        <LoadingAnimation translatedMessages={loadingMessages} />
      ) : (
        <div className="vai-flex-1 vai-overflow-auto">
          {insPage === 'dev-tool' ? (
            <NarrativeDevTool />
          ) : (
            <div className="vai-flex vai-flex-row vai-justify-between vai-h-full vai-gap-4">
              <div className="vai-flex-1 vai-h-full vai-overflow-hidden">
                <div className="vai-flex vai-flex-col vai-h-full">
                  <div className="vai-sticky vai-z-10 vai-w-full">
                    {/* Zoom Tool Topbar */}
                    <div className="vai-flex vai-justify-center vai-w-full">
                      <ZoomToolTopbar
                        contentRef={contentRef}
                        componentRef={
                          componentRef as React.RefObject<HTMLDivElement>
                        }
                        scale={scale}
                        selectedTreatmentIds={selectedTreatmentIds}
                        claimSummary={claimSummary}
                        hasInitiallyScaled={hasInitiallyScaled}
                        onSetHasInitiallyScaled={setHasInitiallyScaled}
                        onSetScale={setScale}
                      />
                    </div>
                    {/* Rate this attachment section */}
                    <div className="vai-absolute vai-right-4 vai-top-0 vai-bottom-0 vai-flex vai-items-center">
                      {preferences?.isFeedbackWidgetEnabled && (
                        <FeedbackWidget
                          onRatingButtonClick={handleAttachmentButtonClick}
                          onRate={handleSubmitAttachmentFeedback}
                          label={<Trans>Rate this attachment</Trans>}
                          upOptions={RATE_ATTACHMENT_OPTIONS_UP}
                          downOptions={RATE_ATTACHMENT_OPTIONS_DOWN}
                        />
                      )}
                    </div>
                  </div>
                  {/* Content Container */}
                  <div className="vai-flex-1 vai-w-full vai-overflow-auto">
                    <div
                      ref={contentRef}
                      style={{
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        height: `${contentHeight}px`,
                      }}
                    >
                      <div
                        className="vai-p-4 vai-pt-0 vai-min-w-fit"
                        ref={componentRef as React.RefObject<HTMLDivElement>}
                      >
                        {/* Claim Attachment */}
                        <ClaimAttachmentContent
                          claimSummary={claimSummary}
                          isLoadingClaimSummary={isLoadingClaimSummary}
                          hasToothLevelSummary={hasToothLevelSummary}
                          hasQuadLevelSummary={hasQuadLevelSummary}
                          filteredColorImages={filteredColorImages}
                          filteredRadiographs={filteredRadiographs}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Claim Customization Sidebar */}
              <ClaimCustomizationSidebar
                defaultTreatments={claimSummaryTreatments}
                onSelectTreatments={handleSelectTreatments}
                claimSummary={claimSummary}
              />
            </div>
          )}
        </div>
      )}
      <Toaster />
    </div>
  );
};

// Export the wrapper component instead to include context provider
export { ClaimSummaryPageWrapper as ClaimSummaryPage };
